import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, Phone, Globe, Clock, Share2, Tag, ChevronLeft, Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import OfferCard from "@/components/OfferCard";
import OfferGallery from "@/components/OfferGallery";
import ShareButton from "@/components/ShareButton";
import { useSeo } from "@/hooks/useSeo";
import { buildKeywords } from "@/components/KeywordsSuggestBlock";
import { SuggestLink } from "@/components/SuggestLink";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import FbGroupShare from "@/components/FbGroupShare";
import DemoBanner from "@/components/DemoBanner";

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [rid, setRid] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("offre_expiree");
  const [reportDetail, setReportDetail] = useState("");

  const submitReport = async () => {
    try {
      await api.post(`/offers/${id}/report`, { reason: reportReason, detail: reportDetail });
      toast.success("Merci, votre signalement a bien été transmis à notre équipe.");
      setReportOpen(false); setReportDetail("");
    } catch {
      toast.error("Le signalement n'a pas pu être envoyé.");
    }
  };

  useEffect(() => {
    api.get(`/offers/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  const _o = data && data.offer;
  useSeo({
    title: _o ? `${_o.discount ? _o.discount + " · " : ""}${_o.title}${_o.business?.name ? " — " + _o.business.name : ""} | PROXIreduc-28` : undefined,
    description: _o ? (_o.description || `Bon plan local à ${_o.city || "Eure-et-Loir"} sur PROXIreduc-28.`).slice(0, 200) : undefined,
    canonicalPath: `/offre/${id}`,
    image: _o ? ((_o.images && _o.images[0]) || _o.image || _o.business?.cover_image || "/og-bons-plans.jpg") : "/og-bons-plans.jpg",
    keywords: _o ? buildKeywords(_o.category?.name, _o.city).join(", ") : undefined,
    type: "article",
  });

  if (data === null) return <div className="p-10 text-center text-slate-500">Chargement…</div>;
  if (data === false) return <div className="p-10 text-center text-slate-500">Offre introuvable.</div>;
  const { offer, similar } = data;

  const redeem = async () => {
    if (!user || user === false) {
      toast.info("Créez votre compte gratuit pour profiter de cette offre.");
      navigate(`/inscription?next=${encodeURIComponent(`/offre/${id}`)}`);
      return;
    }
    try {
      await api.post(`/offers/${id}/click`);
      const { data } = await api.post(`/offers/${id}/redeem`);
      setCode(data.redemption_code);
      setRid(data.redemption_id);
      toast.success("Votre code-barres est prêt — présentez-le au commerçant.");
    } catch (e) { toast.error("Impossible de générer le code."); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: offer.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); }
    } catch {}
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/bons-plans" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ChevronLeft size={14}/> Retour</Link>
      <div className="mt-4 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <OfferGallery offer={offer} />
          <div className="mt-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-extrabold tracking-wide bg-rose-500 text-white">{offer.discount}</span>
              <span className="px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">{offer.category?.name}</span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-500"><MapPin size={12}/> {offer.city}</span>
            </div>
            {offer.demo && <DemoBanner className="mt-3" />}
            <h1 className="font-heading text-3xl md:text-4xl font-bold mt-3" data-testid="offer-title">{offer.title}</h1>
            <p className="mt-3 text-slate-700 whitespace-pre-line">{offer.description}</p>
            {offer.conditions && (
              <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <div className="font-semibold mb-1">Conditions</div>
                {offer.conditions}
              </div>
            )}
            {offer.end_date && (
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600"><Clock size={14}/> Valable jusqu'au {new Date(offer.end_date).toLocaleDateString("fr-FR")}</div>
            )}
            <SuggestLink />
            <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Bons plans", path: "/bons-plans" }, { name: offer.title }]} />
          </div>
        </div>

        <aside className="lg:col-span-1 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Partenaire</div>
            <Link to={`/commerce/${offer.business?.id}`} className="mt-1 font-heading text-xl font-bold block hover:text-[#E05638]">{offer.business?.name}</Link>
            <div className="mt-2 text-sm text-slate-600 space-y-1">
              {offer.business?.address && <div className="flex gap-2"><MapPin size={14} className="mt-0.5"/> {offer.business.address}, {offer.business.city}</div>}
              {offer.business?.phone && <div className="flex gap-2"><Phone size={14} className="mt-0.5"/> {offer.business.phone}</div>}
              {offer.business?.website && <div className="flex gap-2"><Globe size={14} className="mt-0.5"/> {offer.business.website}</div>}
              {offer.business?.opening_hours && <div className="flex gap-2"><Clock size={14} className="mt-0.5"/> {offer.business.opening_hours}</div>}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <Button onClick={redeem} className="w-full btn-shine text-white h-11" data-testid="offer-redeem-btn"><Tag className="mr-2" size={16}/> Utiliser cette offre</Button>
            {code && (
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-center" data-testid="redemption-block">
                <div className="text-xs text-emerald-800 font-semibold">Présentez ce code-barres au commerçant</div>
                {rid && (
                  <img src={`${API}/api/offers/redemption/${rid}/qr.png`} alt="Code à scanner par le commerçant"
                       className="mx-auto my-3 w-44 h-44 rounded-lg bg-white p-2 border border-emerald-200" data-testid="redemption-qr" />
                )}
                <div className="font-mono text-sm font-bold text-emerald-900" data-testid="redemption-code">Code : {code}</div>
                <div className="text-[11px] text-emerald-700 mt-1">Le commerçant le scanne avec l'app PROXIreduc pour valider votre avantage.</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <ShareButton
                url={`${API}/api/offre-vitrine/${id}`}
                title={offer.title}
                text={`${offer.discount ? offer.discount + " · " : ""}${offer.title}${offer.business?.name ? " — " + offer.business.name : ""}`}
                testId="offer-share-btn"
                wrapperClassName="w-full"
                className="w-full"
                onShare={(ch) => api.post(`/offers/${id}/share?channel=${ch}`).catch(() => {})}
              />
              <Button variant="outline" data-testid="offer-fav-btn"><Heart size={14} className="mr-1"/> Favoris</Button>
            </div>
            <button onClick={() => setReportOpen(true)} className="w-full text-xs text-slate-400 hover:text-rose-500 inline-flex items-center justify-center gap-1 pt-1" data-testid="offer-report-open">
              <Flag size={12}/> Signaler cette offre
            </button>
            {reportOpen && (
              <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-3 space-y-2" data-testid="offer-report-form">
                <div className="text-xs font-bold text-slate-700">Signaler un problème</div>
                <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="offer-report-reason">
                  <option value="offre_expiree">Offre expirée</option>
                  <option value="reduction_incorrecte">Réduction incorrecte</option>
                  <option value="contenu_incorrect">Contenu incorrect</option>
                  <option value="probleme_partenaire">Problème avec le partenaire</option>
                  <option value="autre">Autre</option>
                </select>
                <textarea value={reportDetail} onChange={(e) => setReportDetail(e.target.value)} rows={2} placeholder="Précisez (optionnel)…" className="w-full rounded-md border border-slate-200 p-2 text-sm" data-testid="offer-report-detail"/>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-rose-600 hover:bg-rose-700 flex-1" onClick={submitReport} data-testid="offer-report-submit">Envoyer</Button>
                  <Button size="sm" variant="outline" onClick={() => setReportOpen(false)}>Annuler</Button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      <FbGroupShare
        text={`${offer.discount ? offer.discount + " · " : ""}${offer.title}${offer.business?.name ? " — " + offer.business.name : ""}`}
        shareUrl={`${API}/api/offre-vitrine/${id}`}
        cityHint={offer.city || offer.business?.city || ""}
      />

      {similar?.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-2xl font-bold mb-4">Offres similaires</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {similar.map((s) => (<OfferCard key={s.id} offer={s}/>))}
          </div>
        </section>
      )}
    </div>
  );
}
